import React, { useState, useMemo, useRef, useEffect, useCallback, memo } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { getCountryName } from '@/lib/translations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

// Country Item Component - Defined outside to prevent re-creation during render
const CountryItem = memo(({ country, index, isFocused, isSelected, onSelect, displayName }) => {
  return (
    <button
      className={cn(
        "w-full flex items-center justify-between p-3 rounded-md text-left transition-colors",
        "hover:bg-accent focus:bg-accent focus:outline-none",
        isFocused && "bg-accent",
        isSelected && "bg-primary/10"
      )}
      onClick={() => onSelect(country)}
      data-testid={`country-${country.code}`}
      data-index={index}
      role="option"
      aria-selected={isSelected}
      id={`country-option-${country.code}`}
      tabIndex={-1}
    >
      <div className="flex items-center gap-3">
        <span className="text-xl" aria-hidden="true">{country.flag}</span>
        <div>
          <div className="font-medium">{displayName}</div>
          <div className="text-xs text-muted-foreground">
            {country.tax_name} • {country.standard_rate}%
          </div>
        </div>
      </div>
      {isSelected && (
        <Check className="w-4 h-4 text-primary" aria-hidden="true" />
      )}
    </button>
  );
});

CountryItem.displayName = 'CountryItem';

// Country Group Component - Defined outside to prevent re-creation during render
const CountryGroup = memo(({ title, countries: groupCountries, startIndex, focusedIndex, selectedCode, onSelect, language }) => {
  if (!groupCountries || groupCountries.length === 0) return null;
  
  return (
    <div className="mb-4" role="group" aria-labelledby={`group-${title.replace(/\s/g, '-')}`}>
      <div 
        className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2"
        id={`group-${title.replace(/\s/g, '-')}`}
      >
        {title} ({groupCountries.length})
      </div>
      <div className="space-y-1" role="listbox">
        {groupCountries.map((country, idx) => {
          const currentIndex = startIndex + idx;
          const displayName = getCountryName(country.code, language) || country.name;
          return (
            <CountryItem 
              key={country.code} 
              country={country} 
              index={currentIndex}
              isFocused={currentIndex === focusedIndex}
              isSelected={selectedCode === country.code}
              onSelect={onSelect}
              displayName={displayName}
            />
          );
        })}
      </div>
    </div>
  );
});

CountryGroup.displayName = 'CountryGroup';

const CountrySelector = ({ countries, selected, onSelect }) => {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Group countries by region
  const groupedCountries = useMemo(() => {
    const euCountries = countries?.filter(c => c.is_eu) || [];
    const nonEuEurope = countries?.filter(c => 
      !c.is_eu && ['GB', 'CH', 'NO', 'IS', 'RS', 'BA', 'AL', 'MK', 'ME', 'UA', 'TR'].includes(c.code)
    ) || [];
    const restOfWorld = countries?.filter(c => 
      !c.is_eu && !['GB', 'CH', 'NO', 'IS', 'RS', 'BA', 'AL', 'MK', 'ME', 'UA', 'TR'].includes(c.code)
    ) || [];

    return {
      eu: euCountries,
      nonEu: nonEuEurope,
      world: restOfWorld
    };
  }, [countries]);

  // Filter by search
  const filteredCountries = useMemo(() => {
    if (!search) return groupedCountries;
    
    const searchLower = search.toLowerCase();
    const filterFn = (c) => 
      c.name.toLowerCase().includes(searchLower) ||
      c.code.toLowerCase().includes(searchLower) ||
      c.tax_name.toLowerCase().includes(searchLower);
    
    return {
      eu: groupedCountries.eu.filter(filterFn),
      nonEu: groupedCountries.nonEu.filter(filterFn),
      world: groupedCountries.world.filter(filterFn)
    };
  }, [groupedCountries, search]);

  // Flat list for keyboard navigation
  const flatList = useMemo(() => [
    ...filteredCountries.eu,
    ...filteredCountries.nonEu,
    ...filteredCountries.world
  ], [filteredCountries]);

  // Focus search on open
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  // Handle selection
  const handleSelect = useCallback((country) => {
    onSelect(country);
    setOpen(false);
    setSearch('');
    setFocusedIndex(-1);
  }, [onSelect]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!open) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < flatList.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : flatList.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && flatList[focusedIndex]) {
          handleSelect(flatList[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(flatList.length - 1);
        break;
      default:
        break;
    }
  }, [open, flatList, focusedIndex, handleSelect]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.querySelector(`[data-index="${focusedIndex}"]`);
      focusedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  const totalFiltered = flatList.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={selected ? `Selected country: ${getCountryName(selected.code, language) || selected.name}, ${selected.standard_rate}% VAT` : t('selectCountry')}
          className="w-full justify-between h-12 text-left font-normal"
          data-testid="country-selector"
        >
          {selected ? (
            <div className="flex items-center gap-3">
              <span className="text-xl" aria-hidden="true">{selected.flag}</span>
              <div>
                <div className="font-medium">{getCountryName(selected.code, language) || selected.name}</div>
                <div className="text-xs text-muted-foreground">
                  {selected.tax_name} • {selected.standard_rate}%
                </div>
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">{t('selectCountry')}</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[350px] p-0" 
        align="start"
        onKeyDown={handleKeyDown}
      >
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Input
              ref={searchInputRef}
              placeholder={t('searchCountry')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setFocusedIndex(-1);
              }}
              className="pl-9"
              data-testid="country-search"
              aria-label="Search countries"
              aria-controls="country-listbox"
              aria-autocomplete="list"
              role="searchbox"
            />
          </div>
          {search && (
            <p className="text-xs text-muted-foreground mt-2" role="status" aria-live="polite">
              {totalFiltered} {totalFiltered === 1 ? 'country' : 'countries'} found
            </p>
          )}
        </div>
        <ScrollArea className="h-[350px] p-3" ref={listRef}>
          <div 
            id="country-listbox"
            role="listbox"
            aria-label="Countries"
            aria-activedescendant={focusedIndex >= 0 ? `country-option-${flatList[focusedIndex]?.code}` : undefined}
          >
            <CountryGroup 
              title={t('euCountries')} 
              countries={filteredCountries.eu}
              startIndex={0}
              focusedIndex={focusedIndex}
              selectedCode={selected?.code}
              onSelect={handleSelect}
              language={language}
            />
            <CountryGroup 
              title={t('nonEuEurope')} 
              countries={filteredCountries.nonEu}
              startIndex={filteredCountries.eu.length}
              focusedIndex={focusedIndex}
              selectedCode={selected?.code}
              onSelect={handleSelect}
              language={language}
            />
            <CountryGroup 
              title={t('restOfWorld')} 
              countries={filteredCountries.world}
              startIndex={filteredCountries.eu.length + filteredCountries.nonEu.length}
              focusedIndex={focusedIndex}
              selectedCode={selected?.code}
              onSelect={handleSelect}
              language={language}
            />
            {totalFiltered === 0 && (
              <div 
                className="text-center text-muted-foreground py-8"
                role="status"
                aria-live="polite"
              >
                No countries found for &ldquo;{search}&rdquo;
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default CountrySelector;
